import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { eNormalizedEntityType, eNormalizedStatus } from '../../enums';
import { Utilities } from '../../utilities';
import { Order } from '../../interfaces/orders/order';
import { TranslocoService } from '@jsverse/transloco';
import { EnumLabelService } from '../../services/enum-label/enum-label.service';

@Component({
    selector: 'lib-ubo-overview',
    templateUrl: './ubo-overview.component.html',
    styleUrls: [
        './ubo-overview.component.css',
        './../../../results-page.css'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UboOverviewComponent {
  private readonly enumLabels = inject(EnumLabelService);
  private readonly transloco = inject(TranslocoService);
  @Input() public order?: Order;
  @Input() public results: unknown | null = null;

  constructor() {}

  // ---------- JSON helpers (unchanged) ----------
  private asRecord(v: unknown): Record<string, unknown> | undefined {
    return (v !== null && typeof v === 'object') ? (v as Record<string, unknown>) : undefined;
  }
  protected get<T = unknown>(path: string): T | undefined {
    const rec = this.asRecord(this.results);
    return rec?.[path] as T;
  }
  protected getFrom<T = unknown>(obj: unknown, key: string): T | string {
    const rec = this.asRecord(obj);
    return rec?.[key] as T ?? "";
  }
  protected getArray<T = unknown>(path: string): T[] {
    const val = this.get(path);
    return Array.isArray(val) ? (val as T[]) : [];
  }
  protected getString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected getEnumString(path: string): string | undefined {
    const v = this.get(path);
    return typeof v === 'string' ? v : undefined;
  }
  protected spaceCase(val: string | undefined): string {
    return Utilities.spacePascalCase(val ?? "");
  }

  // ---------- Display helpers (unchanged from your version) ----------
  protected getStatusIcon(): string { /* same as yours */ 
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return 'check_circle_outline';
      case eNormalizedStatus.inactive: return 'block';
      default: return '';
    }
  }
  protected getStatusIconColor(): { color: string } {
    const status = this.getEnumString('normalizedStatus') as eNormalizedStatus | undefined;
    switch (status) {
      case eNormalizedStatus.active: return { color: '#5C7F46' };
      case eNormalizedStatus.inactive: return { color: '#FB2C38' };
      default: return { color: 'black' };
    }
  }
  protected getTypeIcon(): string {
    const type = this.getEnumString('normalizedEntityType') as eNormalizedEntityType | undefined;
    switch (type) {
      case eNormalizedEntityType.businessName: return 'domain';
      case eNormalizedEntityType.corporation: return 'finance';
      default: return '';
    }
  }
  protected addressTypeLabel(address: unknown): string {
    const type = this.getFrom<string>(address, 'normalizedAddressType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.address');
    return this.enumLabels.label('normalizedAddressType', type);
  }
  protected partyTypeLabel(party: unknown): string {
    const type = this.getFrom<string>(party, 'normalizedPartyType');
    if (!type || type === 'none') return this.transloco.translate('common.labels.none');
    return this.enumLabels.label('normalizedPartyType', type);
  }
  protected getCivicAddress(party: unknown): string | undefined {
    const addresses = this.getFrom(party, 'addresses');
    if (Array.isArray(addresses) && addresses.length > 0) {
      const first = addresses[0];
      return typeof this.getFrom(first, 'fullAddress') === 'string'
        ? (this.getFrom(first, 'fullAddress') as string)
        : '';
    }
    return '';
  }
}